import { useLiveQuery } from "dexie-react-hooks";
import { TRUCK_DEFAULTS, newId } from "@haulsheet/core";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { TextInput } from "../components/ui/Field";
import { getDb } from "../db/dexie";
import { useAuthStore } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";
import { useUnsavedGuard } from "../pwa/unsaved";

export function SettingsTruck() {
  const me = useAuthStore((s) => s.me);
  const truck = useLiveQuery(
    () => getDb().truck.filter((t) => t.deletedAt == null).first(),
    [],
  );
  const [mpg, setMpg] = useState("");
  const [unit, setUnit] = useState("");
  /** Result of the last Save: confirmation or a plain error (was silent). */
  const [status, setStatus] = useState<{ kind: "saved" } | { kind: "error"; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (truck) {
      setMpg(String(truck.mpg));
      setUnit(truck.unitNumber ?? "");
    }
  }, [truck]);
  useUnsavedGuard(
    "settings-truck",
    truck ? mpg !== String(truck.mpg) || unit !== (truck.unitNumber ?? "") : mpg.trim() !== "" || unit.trim() !== "",
  );

  async function save() {
    if (!me) return;
    setStatus(null);
    const mpgN = Number(mpg.trim());
    if (!mpg.trim() || !Number.isFinite(mpgN) || mpgN < 1 || mpgN > 30) {
      setStatus({ kind: "error", message: "MPG needs to be a number between 1 and 30, like 6.5." });
      return;
    }
    setSaving(true);
    try {
      await writeTruck(mpgN);
      setStatus({ kind: "saved" });
    } catch (e) {
      setStatus({
        kind: "error",
        message: `Couldn't save on this phone${e instanceof Error && e.message ? `: ${e.message}` : ""}. Try again.`,
      });
    } finally {
      setSaving(false);
    }
  }

  async function writeTruck(mpgN: number) {
    if (!me) return;
    const now = new Date().toISOString();
    if (truck) {
      await getSyncClient().mutate({
        entity: "truck",
        op: "upsert",
        id: truck.id,
        baseVersion: truck.version || null,
        patch: { mpg: mpgN, unitNumber: unit.trim() || null },
        row: {
          ...truck,
          mpg: mpgN,
          unitNumber: unit.trim() || null,
          updatedAt: now,
        },
      });
    } else {
      const id = newId();
      await getSyncClient().mutate({
        entity: "truck",
        op: "upsert",
        id,
        baseVersion: null,
        row: {
          id,
          orgId: me.org.id,
          version: 0,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
          driverId: me.driverId,
          unitNumber: unit.trim() || null,
          ...TRUCK_DEFAULTS,
          mpg: mpgN,
        },
      });
    }
  }

  return (
    <div className="stack">
      <Link to="/settings" className="muted back-link">
        ← Settings
      </Link>
      <h1 className="screen-title">Truck</h1>
      <div className="card stack">
        <TextInput
          label="Unit #"
          placeholder="Unit number"
          value={unit}
          onChange={(e) => {
            setUnit(e.target.value);
            setStatus(null);
          }}
        />
        <TextInput
          label="MPG"
          placeholder="6"
          inputMode="decimal"
          value={mpg}
          onChange={(e) => {
            setMpg(e.target.value);
            setStatus(null);
          }}
        />
        {status?.kind === "saved" ? (
          <p className="legal-ok" role="status" data-testid="truck-saved" style={{ margin: 0 }}>
            Saved. It syncs to your other devices when you&apos;re online.
          </p>
        ) : null}
        {status?.kind === "error" ? (
          <p className="legal-flag" role="alert" data-testid="truck-error" style={{ margin: 0 }}>
            {status.message}
          </p>
        ) : null}
        <Button variant="primary" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
